import React from "react";
import { HiExternalLink, HiPlay } from "react-icons/hi";
import { FaRobot, FaGraduationCap, FaCertificate, FaTrophy, FaInstagram } from "react-icons/fa";

const HexaPiClub = () => {
  const programs = [
    {
      title: "Young Engineers",
      age: "5-8 years",
      description: "Robot programming fundamentals and AI-controlled robotics introduction",
    },
    {
      title: "Training Innovators",
      age: "9-12 years",
      description: "Advanced programming, LEGO EV3 robotics, and 3D printing basics",
    },
    {
      title: "Future Minds",
      age: "13-18 years",
      description: "Arduino programming, robotics projects, and electrical circuits",
    },
    {
      title: "Tech Leaders",
      age: "18-25 years",
      description: "Advanced AI, machine learning, web development, and IoT systems",
    },
  ];

  const features = [
    { icon: FaRobot, text: "State-of-the-art AI & Robotics Lab" },
    { icon: FaGraduationCap, text: "Age-appropriate Programs (5-25)" },
    { icon: FaCertificate, text: "Certificates by HexaPi Tech" },
    { icon: FaTrophy, text: "National Robotics Championships" },
  ];

  return (
    <section id="hexapi-club" className="py-10 lg:py-14 bg-gradient-to-br from-primary-600 to-primary-700">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 bg-white/20 text-white text-sm font-medium rounded-full mb-4">
            New Initiative
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Introducing HexaPi Club
          </h2>
          <div className="w-20 h-1 bg-white mx-auto mb-4 rounded-full"></div>
          <p className="text-primary-100 max-w-2xl mx-auto">
            Our new AI and Robotics Educational Institution — preparing young minds to become future innovators
            through hands-on experiences in technology and engineering.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-10">
          {/* Left: Image & Instagram Reels */}
          <div className="space-y-4">
            {/* Club Image */}
            <div className="bg-white/10 rounded-2xl overflow-hidden aspect-video">
              <img
                src="/images/club.png"
                alt="HexaPi Club"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Instagram Reels */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {[
                { id: "DM8u8LGz52-", cover: "/images/reels/reel-1.jpg", visible: "flex" },
                { id: "DSakuZsjdJC", cover: "/images/reels/reel-2.jpg", visible: "hidden lg:flex" },
                { id: "DS-ZHa9DS-W", cover: "/images/reels/reel-3.jpg", visible: "hidden lg:flex" },
              ].map((reel) => (
                <a
                  key={reel.id}
                  href={`https://www.instagram.com/reel/${reel.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${reel.visible} group rounded-2xl overflow-hidden relative items-center justify-center`}
                  style={{ aspectRatio: '9/16' }}
                >
                  <img
                    src={reel.cover}
                    alt="HexaPi Club Reel"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
                  <div className="relative text-center text-white">
                    <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                      <HiPlay className="w-8 h-8 ml-1" />
                    </div>
                    <FaInstagram className="w-5 h-5 mx-auto" />
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Right: Info & Programs */}
          <div>
            {/* Programs */}
            <h3 className="text-lg font-semibold text-white mb-4">Age-Tiered Programs</h3>
            <div className="space-y-3 mb-6">
              {programs.map((program, index) => (
                <div
                  key={index}
                  className="bg-white/10 rounded-lg p-4 hover:bg-white/15 transition-colors"
                >
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-medium text-white">{program.title}</h4>
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-primary-100">
                      {program.age}
                    </span>
                  </div>
                  <p className="text-primary-200 text-sm">{program.description}</p>
                </div>
              ))}
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-primary-100 text-sm">
                  <feature.icon className="w-4 h-4 text-accent-400" />
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <a
            href="https://hexapi.club"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 font-medium
                       rounded-lg hover:bg-primary-50 transition-colors"
          >
            Visit HexaPi Club
            <HiExternalLink className="w-4 h-4" />
          </a>
          <p className="text-primary-200 text-sm mt-3">
            Proudly sponsored by HexaPi Tech
          </p>
        </div>
      </div>
    </section>
  );
};

export default HexaPiClub;
